import { IValidateError, OptionType } from "shared";
import {
    ComponentTypes,
    ITemplateAutonumberComponent,
    ITemplateComponent,
    ITemplateProcureComponent,
    ITemplateSaveAndTicketComponent
} from "entities/template";
import { useTranslation } from "react-i18next";
import React, { useCallback } from "react";
import { useComponentsTranslations } from "../i18n";
import { IPROC_SUBTYPE_PROP } from "../utils/iproc_integration";
import {
    isAutonumberDateSourceMissing,
    isAutonumberTagDuplicated,
    validateAutonumberCode,
    validateAutonumberMaskSyntax
} from "../utils/autonumber";

interface IValidateComponents extends ITagUniquenessValidate {
    componentsValid: boolean;
    errorComponents: number[];
}

interface ITagUniquenessValidate {
    notUniqueTagsArr: number[],
    unique: boolean;
}

export function useComponentValidateFunction() {
    const t = useComponentsTranslations();
    const { i18n } = useTranslation();

    const validateTagUniqueness = (components: ITemplateComponent[]): ITagUniquenessValidate => {
        const tagNotUnique = new Set<number>();
        components
            .filter(c => ![
                ComponentTypes.trackerIntegration,
                ComponentTypes.iprocIntegration,
                ComponentTypes.saveToExistingTicket,
                ComponentTypes.saveAndTicket,
            ].includes(c.type))
            .forEach((comp, index) => {
                if (comp.name) {
                    const tagComponents = components.filter(c => c.name === comp.name);
                    if (tagComponents.length > 1) {
                        tagComponents.forEach(c => tagNotUnique.add(c.id as number));
                    }
                }
            });
        const notUniqueArr = Array.from(tagNotUnique);
        return { unique: notUniqueArr.length === 0, notUniqueTagsArr: notUniqueArr };
    };

    const validateComponents = useCallback((components: ITemplateComponent[], pdfPassword?: string): IValidateComponents => {
        const errorComponents = new Set<number>();
        let componentsValid = true;

        components.forEach((component) => {
            if (component.type === ComponentTypes.autonumber &&
                (component as ITemplateAutonumberComponent).counterReadOnly) {
                return;
            }

            const validationMapObject = componentValidationMap[component.type];

            if (validationMapObject) {
                if (component.type === ComponentTypes.saveAndTicket) {
                    const { valid } = requiredPdfPassword(component as ITemplateSaveAndTicketComponent, pdfPassword ? pdfPassword : "",);
                    if (!valid) {
                        errorComponents.add(component.id as number);
                        componentsValid = false;
                    }
                }
                Object.keys(validationMapObject).forEach((key) => {
                    // @ts-ignore
                    const componentValue = component[key];
                    const { valid } = validationMapObject[key](componentValue, component);
                    if (!valid) {
                        errorComponents.add(component.id as number);
                        componentsValid = false;
                    }
                });
            }

            if (component.type === ComponentTypes.autonumber) {
                const autonumber = component as ITemplateAutonumberComponent;
                if (
                    isAutonumberTagDuplicated(autonumber, components) ||
                    isAutonumberDateSourceMissing(autonumber, components) ||
                    autonumber.counterLoadError
                ) {
                    errorComponents.add(component.id as number);
                    componentsValid = false;
                }
            }
        });
        // Выключаем валидацию на уникальность тега
        // const tagUniquenessValid = validateTagUniqueness(components);
        // if (!tagUniquenessValid.unique) {
        //     tagUniquenessValid.notUniqueTagsArr.forEach((id) => {
        //         errorComponents.add(id);
        //     })
        // }

        const tagUniquenessValid = { unique: true, notUniqueTagsArr: [] };

        return {
            componentsValid: componentsValid,
            errorComponents: Array.from(errorComponents), ...tagUniquenessValid
        };
    }, []);

    const requiredFieldTicketNum = useCallback((value: string, component: ITemplateProcureComponent): IValidateError => {
        if (component.isTemplate) {
            return requiredFieldValidation(value);
        } else {
            return {
                valid: true
            };
        }
    }, [i18n.language]);

    const requiredFieldRequestStyle = useCallback((value: string, component: ITemplateProcureComponent): IValidateError => {
        if (component.hideIprocStyle) {
            return requiredFieldValidation(value);
        } else {
            return {
                valid: true
            };
        }
    }, [i18n.language]);

    const requiredPdfPassword = useCallback((component: ITemplateSaveAndTicketComponent, value: string): IValidateError => {
        if (component.isProtected) {
            return requiredPasswordValidation(value);
        } else {
            return {
                valid: true
            };
        }
    }, [i18n.language]);

    const noSpacesValidation = useCallback((value: string): IValidateError => {
        if (value.match(/\s/g)) {
            return {
                valid: false,
                message: t("noSpacesAllowed")
            };
        } else {
            return {
                valid: true
            };
        }
    }, [i18n.language]);

    const requiredPasswordValidation = useCallback((value: string): IValidateError => {
        if (!value || (Array.isArray(value) && value.length === 0)) {
            return {
                valid: false,
                message: t("requiredFieldError")
            };
        }

        return notRequiredPasswordValidation(value);
    }, [i18n.language]);

    const notRequiredPasswordValidation = useCallback((value: string): IValidateError => {
        if (value && value.match(/^\s*$/g)) {
            return {
                valid: false,
                message: t("passwordOnlySpacesNotAllowed")
            };
        }
        if (value && value.match(/^( |\s)|(\s| )$/g)) {
            return {
                valid: false,
                message: t("passwordShouldNotStartEndSpaces")
            };
        }
        return {
            valid: true
        };
    }, [i18n.language]);

    const tagFormatValidation = useCallback((value: string): IValidateError => {
        if (value.match(/^[\p{L}_][\p{L}0-9_]*$/u)) {
            return {
                valid: true,
            };
        } else {
            return {
                valid: false,
                message: t("invalidTagFormat")
            };
        }
    }, [i18n.language]);

    const requiredFieldSelectValidate = useCallback((val: OptionType | OptionType[] | undefined) => {
        if (!val || (Array.isArray(val) && val.length === 0)) {
            return {
                valid: false,
                message: t("requiredFieldError")
            };
        } else {
            return {
                valid: true
            };
        }
    }, [i18n.language]);

    const requiredListFieldValidation = useCallback((value: string): IValidateError => {
        if (!value || (Array.isArray(value) && value.length === 0)) {
            return {
                valid: false,
                message: t("addField")
            };
        } else {
            return {
                valid: true
            };
        }
    }, [i18n.language]);

    const requiredFieldValidation = useCallback((value: string): IValidateError => {
        if (!value) {
            return {
                valid: false,
                message: t("requiredFieldError")
            };
        } else {
            return {
                valid: true
            };
        }
    }, [i18n.language]);

    const validateListNotEmpty = useCallback((value: string[], isTable: boolean): IValidateError => {
        if (!value || value.length === 0) {
            return {
                valid: false,
                message: isTable ? t("tableRequiredColumnError") : t("listRequired")
            };
        } else {
            return {
                valid: true
            };
        }
    }, [i18n.language]);

    const validateTag = useCallback((value: string): IValidateError => {
        const isRequired = requiredFieldValidation(value);
        if (!isRequired.valid) {
            return isRequired;
        }
        const noSpaces = noSpacesValidation(value);
        if (!noSpaces.valid) {
            return noSpaces;
        }
        const tagValidation = tagFormatValidation(value);
        if (!tagValidation.valid) {
            return tagValidation;
        }
        return {
            valid: true
        };
    }, [i18n.language]);

    const validateList = useCallback((value: string[]): IValidateError => {
        const listNotEmpty = validateListNotEmpty(value, false);
        if (!listNotEmpty.valid) {
            return listNotEmpty;
        }
        return {
            valid: true
        };
    }, [i18n.language]);

    const validateTableList = useCallback((value: string[]): IValidateError => {
        const listNotEmpty = validateListNotEmpty(value, true);
        if (!listNotEmpty.valid) {
            return listNotEmpty;
        }
        return {
            valid: true
        };
    }, [i18n.language]);

    const componentValidationMap: Record<ComponentTypes, Record<string, Function> | undefined> = {
        [ComponentTypes.input]: {
            name: validateTag
        },
        [ComponentTypes.text]: undefined,
        [ComponentTypes.dateInput]: {
            name: validateTag
        },
        [ComponentTypes.select]: {
            name: validateTag,
            list: validateList
        },
        [ComponentTypes.inputWithSelect]: {
            name: validateTag,
            list: validateList
        },
        [ComponentTypes.money]: {
            name: validateTag
        },
        [ComponentTypes.checkbox]: {
            name: validateTag
        },
        [ComponentTypes.radio]: {
            name: validateTag,
            list: validateList
        },
        [ComponentTypes.multiline]: {
            name: validateTag
        },
        [ComponentTypes.table]: {
            name: validateTag,
            list: validateTableList
        },
        [ComponentTypes.file]: {
            name: validateTag
        },
        [ComponentTypes.hrPerson]: {
            fieldsList: requiredListFieldValidation
        },
        [ComponentTypes.yandex]: {
            fieldsList: requiredListFieldValidation
        },
        [ComponentTypes.legalYandex]: {
            fieldsList: requiredListFieldValidation
        },
        [ComponentTypes.counterparty]: {
            fieldsList: requiredListFieldValidation
        },
        [ComponentTypes.saveAndTicket]: {
            startrekQueue: requiredFieldValidation
        },
        [ComponentTypes.saveToExistingTicket]: {
            startrekQueueAttach: requiredFieldValidation
        },
        [ComponentTypes.pdfPassword]: undefined,
        [ComponentTypes.trackerIntegration]: {
            startrekFields: requiredFieldSelectValidate
        },
        [ComponentTypes.iprocIntegration]: {
            startrekQueueIproc: requiredFieldSelectValidate,
            defaultValue: requiredFieldRequestStyle,
            ticketNum: requiredFieldTicketNum
        },
        [ComponentTypes.externalForm]: undefined,
        [ComponentTypes.language]: undefined,
        [ComponentTypes.uuid]: {
            name: validateTag
        },
        [ComponentTypes.barcode]: {
            name: validateTag,
            ratio: requiredFieldValidation
        },
        [ComponentTypes.autonumber]: {
            counterName: requiredFieldValidation,
            name: validateTag,
            mask: (value: string) => {
                const result = validateAutonumberMaskSyntax(value);
                return {
                    valid: result.valid,
                    message: result.error ? t(`autonumber.errors.${result.error}` as any) : undefined
                };
            },
            code: (value: string, component: ITemplateAutonumberComponent) => {
                const result = validateAutonumberCode(component.mask, value);
                return {
                    valid: result.valid,
                    message: result.valid ? undefined : t("requiredFieldError")
                };
            }
        },
        [ComponentTypes.preview]: undefined,
        [ComponentTypes.massDocGeneration]: {
            startrekQueue: requiredFieldValidation
        }
    };


    return {
        validateTag,
        validateComponents,
        requiredFieldValidation,
        requiredPasswordValidation,
        notRequiredPasswordValidation,
        validateList,
        validateTableList
    };
}
